import {
  List, Edit, Create, SimpleForm, TextInput, BooleanInput, required,
} from 'react-admin';
import { Stack, Typography, Chip, Alert } from '@mui/material';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import { ReorderableDatagrid } from '../../components/ReorderableDatagrid.jsx';

/**
 * Classes offered in the Admission page's "Applying for class" dropdown, and
 * who receives the notification email for each. Enquiries are always saved to
 * Inbox → Admission enquiries; the email is the extra heads-up.
 */

const emailsCell = (r) => (r.notifyEmails
  ? r.notifyEmails
  : <Chip size="small" variant="outlined" label="Default inbox" />);

const AdmissionClassList = () => (
  <List
    sort={{ field: 'sortOrder', order: 'ASC' }}
    perPage={100}
    pagination={false}
    empty={false}
    title="Admission classes & enquiry emails"
  >
    <Alert severity="info" sx={{ m: 1 }}>
      Each class below appears in the <strong>Applying for class</strong> dropdown on the
      Admission page. When a parent submits an enquiry, it is saved to Inbox → Admission
      enquiries and emailed to that class&apos;s recipients. Classes without recipients go
      to the school&apos;s default notification inbox.
    </Alert>
    <ReorderableDatagrid
      columns={[
        { key: 'name', header: 'Class', width: 180, render: (r) => r.name },
        { key: 'notifyEmails', header: 'Enquiry emails go to', render: emailsCell },
        { key: 'isActive', header: 'On form', width: 100, render: (r) => (r.isActive ? '✓' : '—') },
      ]}
    />
  </List>
);

// Mirrors the server-side check in routes/admin.js so typos are caught inline.
const validateEmails = (value) => {
  const bad = String(value ?? '').split(/[\s,;]+/).filter(Boolean)
    .filter((e) => !/^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/.test(e));
  return bad.length ? `Not a valid email address: ${bad.join(', ')}` : undefined;
};

const AdmissionClassForm = () => (
  <Stack spacing={2} sx={{ maxWidth: 720 }}>
    <TextInput
      source="name"
      label="Class name"
      validate={required()}
      fullWidth
      helperText='Shown to parents exactly as typed — e.g. "Play Group", "KG I", "Class VII".'
    />
    <TextInput
      source="notifyEmails"
      label="Send enquiries to"
      validate={validateEmails}
      multiline
      minRows={2}
      fullWidth
      helperText="One or more email addresses, separated by commas or new lines. Leave blank to use the school's default notification inbox."
    />
    <BooleanInput
      source="isActive"
      label="Show on the admission form"
      defaultValue
      helperText="Turn off to hide this class from parents without deleting it."
    />
    <Typography variant="caption" color="text.secondary">
      Drag rows on the list page to change the order parents see in the dropdown.
    </Typography>
  </Stack>
);

const AdmissionClassEdit = () => (
  <Edit redirect="list" mutationMode="pessimistic">
    <SimpleForm><AdmissionClassForm /></SimpleForm>
  </Edit>
);

const AdmissionClassCreate = () => (
  // New classes go to the end of the dropdown; drag to reposition.
  <Create redirect="list" transform={(data) => ({ sortOrder: 999, ...data })}>
    <SimpleForm><AdmissionClassForm /></SimpleForm>
  </Create>
);

export default {
  list: AdmissionClassList,
  edit: AdmissionClassEdit,
  create: AdmissionClassCreate,
  icon: ForwardToInboxIcon,
  options: { label: 'Admission classes' },
  recordRepresentation: 'name',
};
